/**
 * Apply a recovered/entered field value into a stored juror and re-derive the
 * scored profile from the full questionnaire. Shared by the background engine
 * (auto-repair) and the detail view (manual entry), so the mapping lives in one
 * place. Fires a `juror-updated` event so the panel/detail can repopulate live.
 */
import { store } from '@/app/store';
import { scrubbedToJuror } from '@/ocr/questionnaire-map';

/**
 * Write `value` into juror `jurorId`'s questionnaire field `fieldId`, refresh the
 * scored fields, and persist. Returns false if the juror no longer exists.
 */
export function applyRecoveredField(jurorId: number, fieldId: string, value: string): boolean {
  const j = store.getState().jurors.find((x) => x.id === jurorId);
  if (!j) return false;
  const q = { ...(j.questionnaire || {}) };
  q[fieldId] = value;
  const m = scrubbedToJuror(q, 0); // re-derive scored fields from the full map
  j.gender = m.gender ?? j.gender;
  j.age = m.age ?? j.age;
  j.edu = m.edu ?? j.edu;
  j.empStatus = m.empStatus ?? j.empStatus;
  j.bizType = m.bizType ?? j.bizType;
  j.occ = m.occ ?? j.occ;
  j.parent = m.parent ?? j.parent;
  j.spouseAge = m.spouseAge ?? j.spouseAge;
  j.spouseEdu = m.spouseEdu ?? j.spouseEdu;
  j.jury = m.jury ?? j.jury;
  j.le = m.le ?? j.le;
  j.lawEmp = m.lawEmp ?? j.lawEmp;
  j.victim = m.victim ?? j.victim;
  j.accused = m.accused ?? j.accused;
  j.fair = m.fair ?? j.fair;
  j.questionnaire = q;
  store.save();
  try {
    window.dispatchEvent(new CustomEvent('juror-updated', { detail: { jurorId } }));
  } catch {
    /* CustomEvent unsupported — UI refreshes on next open */
  }
  return true;
}
